/** Why the forecast grid couldn't be loaded. The grid is proxied from Open-Meteo's free tier, which
 *  rate-limits by weighted location count and occasionally stalls on the heavy multi-location
 *  request — so every reason here is transient and (bar `unexpected`) upstream of us. The weather
 *  overlay is optional, which is why these are explained quietly beside its controls and retried,
 *  never raised as an app-wide error. */
export type WeatherUnavailableReason = "timed-out" | "rate-limited" | "provider-error" | "unexpected";

const EXPLANATIONS: Record<WeatherUnavailableReason, string> = {
	"timed-out": "The weather provider (Open-Meteo) timed out.",
	"rate-limited": "The weather provider (Open-Meteo) is rate-limiting requests.",
	"provider-error": "The weather provider (Open-Meteo) returned an error.",
	unexpected: "The weather forecast couldn't be loaded.",
};

/** The one-sentence, user-facing account of a {@link WeatherUnavailableReason}. */
export function describeWeatherUnavailable(reason: WeatherUnavailableReason): string {
	return EXPLANATIONS[reason];
}

/** A failed grid fetch, named by why it failed. Its `message` is the user-facing explanation. */
export class WeatherUnavailableError extends Error {
	constructor(
		readonly reason: WeatherUnavailableReason,
		options?: ErrorOptions,
	) {
		super(describeWeatherUnavailable(reason), options);
		this.name = "WeatherUnavailableError";
	}
}
